import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Ban } from "lucide-react";

const TargetAudience = () => {
    const idealUsers = [
        "Traders who want precision without indicators",
        "Professionals who want predictive tools",
        "Investors who want timing clarity before entries",
        "Crypto/Forex/Index traders needing non-emotional trading",
        "Serious learners seeking a unique, rare, global-class edge"
    ];

    const notForUsers = [
        "Indicator collectors",
        "Gamble-traders",
        "People looking for shortcuts"
    ];

    return (
        /*
           FIX 1: Adjusted Padding
           - lg:px-10 (was px-20): Gives more horizontal space on iPad Pro (1024px).
           - xl:px-20: Restores the wide padding for large desktops.
        */
        <section id="mentorship" className="w-full py-24 px-4 sm:px-10 lg:px-10 xl:px-20 font-poppins overflow-hidden">
            <div className="max-w-7xl mx-auto">

                {/* HEADER */}
                <div className="text-center mb-16">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
                        <span className="text-xs font-bold tracking-widest text-primary uppercase">
                            Exclusive Mentorship
                        </span>
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-3xl md:text-5xl font-bold text-dark dark:text-light"
                    >
                        Who Is This For?
                    </motion.h2>
                </div>

                {/*
                   FIX 2: Grid Strategy
                   - grid-cols-1: Mobile/Tablet Portrait (Stacked)
                   - lg:grid-cols-2: iPad Pro/Small Laptop (50/50 Split - cleaner look)
                   - xl:grid-cols-5: Desktop (60/40 Split - original design)
                */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-8 items-stretch">

                    {/* LEFT: THE IDEAL CANDIDATE */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        /* FIX 3: Column Spanning
                           - lg:col-span-1 (Takes 1/2 space)
                           - xl:col-span-3 (Takes 3/5 space on big screens)
                        */
                        className="lg:col-span-1 xl:col-span-3 bg-white dark:bg-bg-dark-2 rounded-2xl p-8 md:p-10 shadow-xl shadow-primary/5 border border-primary/10 relative overflow-hidden"
                    >
                        {/* Decoration */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full pointer-events-none" />

                        <h3 className="text-2xl font-bold text-dark dark:text-light mb-8 flex items-center gap-3">
              <span className="p-2 rounded-lg bg-primary/10 text-primary">
                <CheckCircle2 size={24} />
              </span>
                            Built For The Serious Few
                        </h3>

                        <ul className="space-y-6">
                            {idealUsers.map((item, i) => (
                                <motion.li
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className="flex items-start gap-4"
                                >
                                    <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center text-white">
                                        <CheckCircle2 size={12} />
                                    </div>
                                    <span className="text-lg text-dark/80 dark:text-light/80 font-medium">
                    {item}
                  </span>
                                </motion.li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* RIGHT: THE "NOT FOR" */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        /* FIX 4: Column Spanning
                           - lg:col-span-1 (Takes 1/2 space)
                           - xl:col-span-2 (Takes 2/5 space on big screens)
                        */
                        className="lg:col-span-1 xl:col-span-2 bg-gradient-to-b from-bg-light-2 to-white dark:from-bg-dark-2 dark:to-bg-dark-1 rounded-2xl p-8 md:p-10 border border-danger/10 relative overflow-hidden flex flex-col justify-center"
                    >
                        {/* Red Glow */}
                        <div className="absolute top-0 right-0 w-full h-1 bg-danger/40" />

                        <h3 className="text-xl font-bold text-dark dark:text-light mb-8 flex items-center gap-3">
              <span className="p-2 rounded-lg bg-danger/10 text-danger">
                <Ban size={24} />
              </span>
                            Who Is This <span className="text-danger underline decoration-2 underline-offset-4">Not</span> For?
                        </h3>

                        <ul className="space-y-6">
                            {notForUsers.map((item, i) => (
                                <motion.li
                                    key={i}
                                    className="flex items-center gap-4 opacity-70 hover:opacity-100 transition-opacity"
                                >
                                    <XCircle className="flex-shrink-0 text-danger" size={20} />
                                    <span className="text-base text-dark/60 dark:text-light/60 font-medium line-through decoration-danger/30">
                    {item}
                  </span>
                                </motion.li>
                            ))}
                        </ul>

                        <div className="mt-10 p-4 rounded-lg bg-danger/5 border border-danger/10 text-xs text-danger/80 text-center leading-relaxed">
                            We maintain a high standard of education. We do not offer "get rich quick" schemes.
                        </div>
                    </motion.div>

                </div>
            </div>
        </section>
    );
};

export default TargetAudience;